import { css } from '@emotion/react';
import { toast } from 'react-toastify';
import { Spinner } from '@chakra-ui/react';
import { useState, FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import Takeover, { Props } from '../Takeover';
import { colors, spacing, fontStyles } from '../../styles';
import Button from '../Button';
import { sendFeedback } from '../../apis/backend';

// Utility functions for feedback tracking
const getFeedbackData = () => {
  const today = new Date().toDateString();
  const stored = localStorage.getItem('feedback_tracking');

  if (stored) {
    const data = JSON.parse(stored);
    if (data.date === today) {
      return { count: data.count, date: today };
    }
  }

  return { count: 0, date: today };
};

const updateFeedbackCount = () => {
  const today = new Date().toDateString();
  const currentData = getFeedbackData();
  const newCount = currentData.count + 1;

  localStorage.setItem(
    'feedback_tracking',
    JSON.stringify({
      count: newCount,
      date: today,
    }),
  );

  return newCount;
};

const DAILY_FEEDBACK_LIMIT = 2;

const FeedbackTakeover: FC<Props> = ({ onClose, ...props }) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [email, setEmail] = useState('');
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [isLimitReached, setIsLimitReached] = useState(false);

  const sendFeedbackMutation = useMutation(async (data: { text: string; email?: string }) => sendFeedback(data));

  useEffect(() => {
    const data = getFeedbackData();
    setFeedbackCount(data.count);
    setIsLimitReached(data.count >= DAILY_FEEDBACK_LIMIT);
  }, []);

  const labelStyle = css`
    display: flex;
    flex-direction: column;
    margin-bottom: ${spacing.deci};
    ${fontStyles.cat}

    div {
      margin: 0 0 ${spacing.centi} 0;
    }
  `;

  const alertStyle = css`
    background-color: ${colors.berryAir};
    border: 1px solid ${colors.berry};
    border-radius: 8px;
    padding: ${spacing.regular};
    margin-bottom: ${spacing.deci};
    ${fontStyles.cat}
    color: ${colors.berry};

    html[data-theme='dark'] & {
      background-color: ${colors.berry}20;
      border-color: ${colors.berry};
      color: ${colors.berry};
    }
  `;

  const handleClose = () => {
    onClose();
    setText('');
    setEmail('');
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (isLimitReached) {
      toast(t('feedback.limitReachedToast'));
      return;
    }

    localStorage.setItem('user_accepted_review', 'true');
    sendFeedbackMutation.mutate(
      { text, email },
      {
        onSuccess: () => {
          const newCount = updateFeedbackCount();
          setFeedbackCount(newCount);
          setIsLimitReached(newCount >= DAILY_FEEDBACK_LIMIT);
          handleClose();
          toast(t('feedback.sentSuccess'));
        },
        onError: () => {
          toast(t('feedback.sendError'));
        },
      },
    );
  };

  return (
    <Takeover
      {...props}
      onClose={handleClose}
      title={t('feedback.giveFeedbackDirectly')}
      name={t('feedback.giveFeedback')}
    >
      <form
        noValidate
        css={css`
          margin: 0;
          display: flex;
          flex-direction: column;
          height: 100%;
        `}
        onSubmit={handleSubmit}
      >
        {isLimitReached && (
          <div css={alertStyle}>
            <strong>{t('feedback.limitReached')}</strong>
            <br />
            {t('feedback.limitReachedMessage', { count: feedbackCount, limit: DAILY_FEEDBACK_LIMIT })}
          </div>
        )}

        {!isLimitReached && feedbackCount > 0 && (
          <div
            css={css`
              ${fontStyles.cat}
              color: ${colors.silver};
              margin-bottom: ${spacing.deci};
              text-align: center;

              html[data-theme='dark'] & {
                color: ${colors.cloud};
              }
            `}
          >
            {t('feedback.sentToday', { count: feedbackCount, limit: DAILY_FEEDBACK_LIMIT })}
          </div>
        )}

        <label css={labelStyle}>
          <div>{t('feedback.emailOptional')}</div>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="example@example.com"
            disabled={isLimitReached}
            css={css`
              width: 100%;
              margin-bottom: ${spacing.centi};
              opacity: ${isLimitReached ? 0.6 : 1};
              cursor: ${isLimitReached ? 'not-allowed' : 'text'};
            `}
          />
        </label>
        <label
          css={css`
            ${labelStyle}
            flex: 1;
          `}
        >
          <div>{t('feedback.feedback')}</div>
          <textarea
            placeholder={t('feedback.placeholder')}
            value={text}
            onChange={(event) => setText(event.target.value)}
            disabled={isLimitReached}
            css={css`
              resize: none;
              width: 100%;
              flex: 1;
              opacity: ${isLimitReached ? 0.6 : 1};
              cursor: ${isLimitReached ? 'not-allowed' : 'text'};
              html[data-theme='dark'] & {
                background-color: ${colors.liquorice};
                color: ${colors.sugar};
              }
            `}
          />
        </label>
        <div
          css={css`
            text-align: right;
          `}
        >
          <Button
            type="submit"
            disabled={!text || sendFeedbackMutation.isLoading || isLimitReached}
            css={css`
              ${fontStyles.mouse}
              padding: ${spacing.regular} 0 ${spacing.regular} ${spacing.regular};
              opacity: ${isLimitReached ? 0.6 : 1};
            `}
          >
            {sendFeedbackMutation.isLoading ? (
              <Spinner color={colors.cloud} size="sm" />
            ) : isLimitReached ? (
              t('feedback.limitReached')
            ) : (
              t('feedback.submitFeedback')
            )}
          </Button>
        </div>
      </form>
    </Takeover>
  );
};

export default FeedbackTakeover;
